import { useMemo } from "react";
import { useApi, apiResolver } from "@/api/useApi";
import { Filter } from "../explore/Explore.filter";
import {
  QaScore,
  QuestionType,
  ReasonForNegativeScore,
} from "../qa-score/QaScore.model";
import { Sources } from "../tracker/Tracker.api";

export type QaRules = Filter | null;

interface SampleQuestion {
  id: number;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
  key: string;
  title: string;
  content: string;
  brief: string | null;
  knowledgeBaseEnabled: boolean;
  includeSources: boolean;
  type: QuestionType;
  sampleFormId: number;
  sampleConfigurationId: number | null;
}

interface SampleConfiguration {
  id: number;
  tenantId: string;
  createdAt: Date;
  updatedAt: Date;
  dateFrom: Date;
  dateTo: Date;
  nextRun: Date;
  isEnabled: boolean;
  daysUntilNextRun: number;
  numberOfTickets: number | null;
  sampleFormId: number | null;
  sampleForm: SampleForm;
  agentGroupId: number | null;
  qaRules: QaRules;
  sources: Sources[];
  tickets: string[];
  conversations: string[];
}

export type SampleFormQaRules = {
  teams?: string[];
  channels?: string[];
} | null;

export interface SampleForm {
  id: number;
  name: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  clientType: QuestionType;
  qaRules?: QaRules;
  questions?: SampleQuestion[];
  sampleConfiguration?: SampleConfiguration[];
}

export interface SampleFormResponse extends Omit<SampleForm, 'qaRules'> {
  qaRules?: QaRules | SampleFormQaRules;
}

export type CreateSampleFormQuestion = {
  key: string;
  title: string;
  content: string;
  type: QuestionType;
  brief?: string;
  includeSources?: boolean;
};

export type CreateSampleFormParams = {
  name: string;
  questions: CreateSampleFormQuestion[];
  qaRules?: QaRules;
};

type CreateSampleFormResponse = Omit<
  SampleForm,
  "questions" | "sampleConfigurations"
>;

type UpdateSampleFormParams = {
  id: number;
  name?: string;
  questions?: CreateSampleFormQuestion[];
  qaRules?: QaRules;
};

type UpdateSampleFormResponse = Omit<
  SampleForm,
  "questions" | "sampleConfigurations"
>;

export type UpsertSampleConfigurationParams = {
  numberOfTickets?: number;
  daysUntilNextRun?: number;
  qaRules?: QaRules;
  sampleFormId?: number;
  isEnabled?: boolean;
  id?: number;
  sources?: Sources[];
};

export type CreateSampleConfigurationsParams = {
  configurations: UpsertSampleConfigurationParams[];
};

export type UpdateTicketScoreRequest = {
  scoreId: number;
  adjustedScore?: number;
  reasonsForNegativeScore?: ReasonForNegativeScore[];
  comment?: string;
};

export type UpdateConversationScoreRequest = {
  scoreId: number;
  adjustedScore: number;
};

export type Sample = {
  id: number;
  name: string;
  tenantId: string;
  createdAt: string;
  dateFrom: string;
  dateTo: string;
  sampleConfigurationId: number;
  sampleConfiguration: SampleConfiguration;
  tickets: string[];
  conversations: string[];
};

function adaptQaRules(sampleForm: SampleFormResponse): SampleForm {
  const qaRules = sampleForm.qaRules;
  if (!qaRules) return {
    ...sampleForm,
    qaRules: {},
  };

  if ('and' in qaRules || 'or' in qaRules)
    return { ...sampleForm, qaRules };

  const filters: Filter[] = [];

  if (typeof qaRules === 'object' && qaRules !== null) {
    if ('channels' in qaRules && Array.isArray(qaRules.channels) && qaRules.channels.length) {
      filters.push({ channel: { in: qaRules.channels } });
    }

    if ('teams' in qaRules && Array.isArray(qaRules.teams) && qaRules.teams.length) {
      filters.push({
        team: { in: qaRules.teams },
      });
    }
  }

  return filters.length ? { ...sampleForm, qaRules: { and: filters } } : { ...sampleForm, qaRules: {} };
}

export function useSamplingApi() {
  const { api } = useApi();

  const sampleApiMethods = useMemo(
    () => ({
      getSampleForms: async () => {
        const res = await apiResolver<SampleFormResponse[]>(api.get(`/platform/sampling/sample-forms`))
        return res.map(adaptQaRules);
      },
      getSampleForm: async (id: number) => {
        const res = await apiResolver<SampleFormResponse>(
          api.get(`/platform/sampling/sample-forms/${id}`)
        )
        return adaptQaRules(res);
      },
      createSampleForm: (params: CreateSampleFormParams) =>
        apiResolver<CreateSampleFormResponse>(
          api.post(`/platform/sampling/sample-forms`, params)
        ),
      updateSampleForm: ({ id, ...params }: UpdateSampleFormParams) =>
        apiResolver<UpdateSampleFormResponse>(
          api.put(`/platform/sampling/sample-forms/${id}`, params)
        ),
      deleteSampleForm: (id: number) =>
        apiResolver<boolean>(
          api.delete(`/platform/sampling/sample-forms/${id}`)
        ),
      getSampleConfigurations: () =>
        apiResolver<SampleConfiguration[]>(
          api.get(`/platform/sampling/sample-configurations`)
        ),
      createSampleConfigurations: (params: CreateSampleConfigurationsParams) =>
        apiResolver<boolean>(
          api.post(`/platform/sampling/sample-configurations`, params)
        ),
      updateSampleConfiguration: (params: UpsertSampleConfigurationParams) =>
        apiResolver<boolean>(
          api.put(`/platform/sampling/sample-configurations`, params)
        ),
      updateTicketScore: (params: UpdateTicketScoreRequest) =>
        apiResolver<QaScore>(
          api.put(`/platform/sampling/ticket-scores`, params)
        ),
      updateConversationScore: (params: UpdateConversationScoreRequest) =>
        apiResolver<QaScore>(
          api.put(`/platform/sampling/conversation-scores`, params)
        ),
      getSamples: () =>
        apiResolver<Sample[]>(api.get(`/platform/sampling/samples`)),
      getSample: (id: number) =>
        apiResolver<Sample>(api.get(`/platform/sampling/v2/samples/${id}`)),
    }),
    [api]
  );

  return sampleApiMethods;
}
